import { type FC } from 'react';
import { web as webRoute } from '@/support/route';
import type { SsoProvider } from './SingleSignOn.types';
import { providerNameMap } from './SingleSignOn.utils';

interface SoftTestSsoLinkProps {
  provider: SsoProvider;
  disabled: boolean;
}

/**
 * Renders a link that opens the per-provider SSO login flow in a new tab so
 * a tenant admin can soft-test their saved configuration (Okta, Azure or
 * SAML2) without enforcing SSO tenant-wide.
 *
 * The href is absolute (https://{team.domain}/api/login/sso/{type}) because
 * the backend issues its callback redirect to the same team-domain origin —
 * an absolute href also makes right-click / middle-click / copy-link work.
 *
 * Disabled until the persisted provider config is saved AND enabled AND the
 * form has no dirty edits — otherwise the admin might be testing a config
 * that does not match what the server has stored.
 */
const SoftTestSsoLink: FC<SoftTestSsoLinkProps> = ({ provider, disabled }) => {
  const href = new URL(webRoute('login.sso', { type: provider }), window.location.origin).href;
  const label = 'Test SSO login';
  const testId = `soft-test-sso-link--${provider}`;
  const disabledTitle = `Enable and save the ${providerNameMap[provider]} configuration to test the SSO login`;

  if (disabled) {
    return (
      <span
        className="moon-gray"
        aria-disabled="true"
        title={disabledTitle}
        data-testid={testId}
        data-test-component="SoftTestSsoLink"
        data-test-element="disabled-link"
        data-test-key={provider}
        style={{ cursor: 'not-allowed' }}
      >
        {label}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="link blue"
      data-testid={testId}
      data-test-component="SoftTestSsoLink"
      data-test-element="enabled-link"
      data-test-key={provider}
    >
      {label}
    </a>
  );
};

export default SoftTestSsoLink;
